import { Address } from "viem"
import { z } from "zod"

import { ethereumAddress } from "../base"

/**
 * Helper Zod transform for "string-or-number" values that Hasura returns as
 * strings but TypeScript wants as numbers. Returns `undefined` for blank,
 * null, or NaN inputs so downstream computeds can decide what to do.
 */
const stringOrNumberToNumber = (fieldName: string) =>
  z.union([z.string(), z.number()]).transform((val) => {
    const num = typeof val === "string" ? Number(val) : val
    if (isNaN(num)) throw new Error(`Invalid ${fieldName}: ${val}`)
    return num
  })

const decimalString = (fieldName: string) =>
  z.union([z.string(), z.number()]).transform((val) => {
    const raw = typeof val === "string" ? val : val.toString()
    if (!/^\d+(?:\.\d+)?$/.test(raw) || !Number.isFinite(Number(raw))) {
      throw new Error(`Invalid ${fieldName}: ${val}`)
    }
    return raw
  })

const optionalStringOrNumberToNumber = (fieldName: string) =>
  z
    .union([z.string(), z.number(), z.null(), z.undefined()])
    .transform((val) => {
      if (val === null || val === undefined) return undefined
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid ${fieldName}: ${val}`)
      return num
    })
    .optional()

/**
 * PoolDayData and PoolHourData share the same interval-aggregate shape —
 * open/close/high/low prices plus volume/fees/tvl. Dates arrive as unix
 * seconds and are converted to numbers.
 *
 * NOTE: `liquidityProviderCount` is always `"0"` in the current indexer
 * release (pending Proposal 1). Do not surface it on any UI until the
 * backfill lands.
 */
const poolIntervalItem = z
  .object({
    date: stringOrNumberToNumber("date").optional(),
    periodStartUnix: stringOrNumberToNumber("periodStartUnix").optional(),
    volumeUSD: stringOrNumberToNumber("volumeUSD"),
    feesUSD: stringOrNumberToNumber("feesUSD"),
    tvlUSD: stringOrNumberToNumber("tvlUSD").optional(),
    // The real indexer exposes `openPrice`; we keep the alias `open` in our
    // downstream types so consumers don't care about the Hasura field name.
    openPrice: stringOrNumberToNumber("openPrice").optional(),
    open: stringOrNumberToNumber("open").optional(),
    close: stringOrNumberToNumber("close").optional(),
    high: stringOrNumberToNumber("high").optional(),
    low: stringOrNumberToNumber("low").optional(),
  })
  .transform((value) => ({
    ...value,
    open: value.open ?? value.openPrice,
  }))

export type PoolIntervalItem = z.infer<typeof poolIntervalItem>

//#region getPools
const manualClPoolsItem = z
  .object({
    address: z
      .string()
      .transform((val) => {
        // Hasura returns addresses with chain ID suffix (e.g., "0x...-8453")
        // Extract just the address part before the hyphen
        return val.split("-")[0]
      })
      .pipe(ethereumAddress),
    feeTier: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid feeTier: ${val}`)
      return num
    }),
    tickSpacing: optionalStringOrNumberToNumber("tickSpacing"),
    feesUsd: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid feesUsd: ${val}`)
      return num
    }),
    token0: z.object({
      address: z
        .string()
        .transform((val) => {
          // Hasura returns addresses with chain ID suffix
          return val.split("-")[0]
        })
        .pipe(ethereumAddress),
      decimals: z.union([z.string(), z.number()]).transform((val) => {
        const num = typeof val === "string" ? Number(val) : val
        if (isNaN(num)) throw new Error(`Invalid decimals: ${val}`)
        return num
      }),
      name: z.string(),
      symbol: z.string(),
    }),
    token0Price: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid token0Price: ${val}`)
      return num
    }),
    token1: z.object({
      address: z
        .string()
        .transform((val) => {
          // Hasura returns addresses with chain ID suffix
          return val.split("-")[0]
        })
        .pipe(ethereumAddress),
      decimals: z.union([z.string(), z.number()]).transform((val) => {
        const num = typeof val === "string" ? Number(val) : val
        if (isNaN(num)) throw new Error(`Invalid decimals: ${val}`)
        return num
      }),
      name: z.string(),
      symbol: z.string(),
    }),
    token1Price: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid token1Price: ${val}`)
      return num
    }),
    volumeUsd: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid volumeUsd: ${val}`)
      return num
    }),
    untrackedVolumeUsd: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid untrackedVolumeUsd: ${val}`)
      return num
    }),
    totalValueLockedUsd: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid totalValueLockedUsd: ${val}`)
      return num
    }),
    totalValueLockedToken0: decimalString("totalValueLockedToken0"),
    totalValueLockedToken1: decimalString("totalValueLockedToken1"),
    liquidityProviderCount: z.string(),
    liquidity: z
      .union([z.string(), z.number(), z.null(), z.undefined()])
      .transform((val) => {
        if (val === null || val === undefined) return "0"
        return typeof val === "string" ? val : val.toString()
      })
      .optional()
      .default("0"),
    tick: z
      .union([z.string(), z.number(), z.null(), z.undefined()])
      .transform((val) => {
        if (val === null || val === undefined) return undefined
        const num = typeof val === "string" ? Number(val) : val
        if (isNaN(num)) return undefined
        return num
      })
      .optional(),
    sqrtPrice: z
      .union([z.string(), z.number(), z.null(), z.undefined()])
      .transform((val) => {
        if (val === null || val === undefined) return undefined
        try {
          return typeof val === "string" ? BigInt(val) : BigInt(Math.floor(val))
        } catch {
          return undefined
        }
      })
      .optional(),
    poolDayData: z.array(poolIntervalItem).optional().default([]),
    poolHourData: z.array(poolIntervalItem).optional().default([]),
  })
  .transform((data) => {
    // Destructure to separate the fields we want to remove / relocate.
    const { token0Price, token1Price, tick, sqrtPrice, poolDayData, poolHourData, ...rest } = data

    return {
      ...rest,
      totalValueLockedToken0: Number(data.totalValueLockedToken0),
      totalValueLockedToken1: Number(data.totalValueLockedToken1),
      totalValueLockedToken0Raw: data.totalValueLockedToken0,
      totalValueLockedToken1Raw: data.totalValueLockedToken1,
      currentTick: tick,
      currentSqrtPriceX96: sqrtPrice,
      poolDayData,
      poolHourData,
      token0: {
        ...data.token0,
        priceUsd: token0Price,
      },
      token1: {
        ...data.token1,
        priceUsd: token1Price,
      },
    }
  })

const manualClPools = z.object({
  pools: z.array(manualClPoolsItem),
})

export const manualClPoolsResponseSchema = manualClPools

export type ManualClPoolsResponse = z.infer<typeof manualClPoolsResponseSchema>
export type ManualClPoolsResponseItem = z.infer<typeof manualClPoolsItem>
//#endregion

//#region getPool
const manualClPoolItemBase = z
  .object({
    id: z.string(),
    address: ethereumAddress,
    feeTier: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid feeTier: ${val}`)
      return num
    }),
    tickSpacing: optionalStringOrNumberToNumber("tickSpacing"),
    feesUSD: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid feesUSD: ${val}`)
      return num
    }),
    token0: z.object({
      id: z.string(),
      address: ethereumAddress,
      decimals: z.union([z.string(), z.number()]).transform((val) => {
        const num = typeof val === "string" ? Number(val) : val
        if (isNaN(num)) throw new Error(`Invalid decimals: ${val}`)
        return num
      }),
      name: z.string(),
      symbol: z.string(),
    }),
    token0Price: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid token0Price: ${val}`)
      return num
    }),
    token1: z.object({
      id: z.string(),
      address: ethereumAddress,
      decimals: z.union([z.string(), z.number()]).transform((val) => {
        const num = typeof val === "string" ? Number(val) : val
        if (isNaN(num)) throw new Error(`Invalid decimals: ${val}`)
        return num
      }),
      name: z.string(),
      symbol: z.string(),
    }),
    tick: z
      .union([z.string(), z.number(), z.null(), z.undefined()])
      .transform((val) => {
        if (val === null || val === undefined) return undefined
        const num = typeof val === "string" ? Number(val) : val
        if (isNaN(num)) return undefined
        return num
      })
      .optional(),
    token1Price: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid token1Price: ${val}`)
      return num
    }),
    volumeUSD: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid volumeUSD: ${val}`)
      return num
    }),
    untrackedVolumeUSD: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid untrackedVolumeUSD: ${val}`)
      return num
    }),
    totalValueLockedUSD: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid totalValueLockedUSD: ${val}`)
      return num
    }),
    totalValueLockedToken0: decimalString("totalValueLockedToken0"),
    totalValueLockedToken1: decimalString("totalValueLockedToken1"),
    liquidityProviderCount: z.union([z.string(), z.number()]).transform((val) => {
      return typeof val === "string" ? val : val.toString()
    }),
    liquidity: z
      .union([z.string(), z.number(), z.null(), z.undefined()])
      .transform((val) => {
        if (val === null || val === undefined) return "0"
        return typeof val === "string" ? val : val.toString()
      })
      .optional()
      .default("0"),
    sqrtPrice: z
      .union([z.string(), z.number(), z.null(), z.undefined()])
      .transform((val) => {
        if (val === null || val === undefined) return undefined
        try {
          return typeof val === "string" ? BigInt(val) : BigInt(Math.floor(val))
        } catch {
          return undefined
        }
      })
      .optional(),
    poolDayData: z.array(poolIntervalItem).optional().default([]),
    poolHourData: z.array(poolIntervalItem).optional().default([]),
  })
  .transform((data) => {
    // Destructure to separate the fields we want to remove / relocate.
    const { token0Price, token1Price, tick, sqrtPrice, poolDayData, poolHourData, ...rest } = data

    return {
      ...rest,
      feesUsd: data.feesUSD,
      volumeUsd: data.volumeUSD,
      untrackedVolumeUsd: data.untrackedVolumeUSD,
      totalValueLockedUsd: data.totalValueLockedUSD,
      totalValueLockedToken0: Number(data.totalValueLockedToken0),
      totalValueLockedToken1: Number(data.totalValueLockedToken1),
      totalValueLockedToken0Raw: data.totalValueLockedToken0,
      totalValueLockedToken1Raw: data.totalValueLockedToken1,
      currentTick: tick,
      currentSqrtPriceX96: sqrtPrice,
      poolDayData,
      poolHourData,
      token0: {
        ...data.token0,
        priceUsd: token0Price,
        address: data.token0.address as Address,
      },
      token1: {
        ...data.token1,
        priceUsd: token1Price,
        address: data.token1.address as Address,
      },
    }
  })

const manualClPoolItem = z.object({
  Pool_by_pk: manualClPoolItemBase.nullable(),
})

export const manualClPoolResponseSchema = manualClPoolItem

export type ManualClPoolResponse = z.infer<typeof manualClPoolResponseSchema>
export type ManualClPoolResponseItem = z.infer<typeof manualClPoolItemBase>
//#endregion

//#region getMyPositions
const manualClMyPositionsItem = z.object({
  id: z.string(),
  chainId: z.union([z.string(), z.number()]).transform((val) => {
    const num = typeof val === "string" ? Number(val) : val
    if (isNaN(num)) throw new Error(`Invalid chainId: ${val}`)
    return num
  }),
  /**
   * NFPM token IDs associated with this Position.
   *
   * Populated by indexer Proposal 4 — until that ships the field returns an
   * empty array. Frontend treats `nfpmTokenIds.length === 0` as "pending"
   * and renders a yellow warning triangle.
   */
  nfpmTokenIds: z
    .array(z.union([z.string(), z.number()]))
    .optional()
    .default([]),
  owner: z.string(),
  tickLower: z.union([z.string(), z.number()]).transform((val) => {
    return typeof val === "string" ? BigInt(val) : BigInt(val)
  }),
  tickUpper: z.union([z.string(), z.number()]).transform((val) => {
    return typeof val === "string" ? BigInt(val) : BigInt(val)
  }),
  liquidity: z.union([z.string(), z.number()]).transform((val) => {
    return typeof val === "string" ? val : val.toString()
  }),
  hasLiquidity: z.boolean(),
  totalDepositedToken0: z.union([z.string(), z.number()]).transform((val) => {
    const num = typeof val === "string" ? Number(val) : val
    if (isNaN(num)) throw new Error(`Invalid totalDepositedToken0: ${val}`)
    return num
  }),
  totalDepositedToken1: z.union([z.string(), z.number()]).transform((val) => {
    const num = typeof val === "string" ? Number(val) : val
    if (isNaN(num)) throw new Error(`Invalid totalDepositedToken1: ${val}`)
    return num
  }),
  totalWithdrawnToken0: z.union([z.string(), z.number()]).transform((val) => {
    const num = typeof val === "string" ? Number(val) : val
    if (isNaN(num)) throw new Error(`Invalid totalWithdrawnToken0: ${val}`)
    return num
  }),
  totalWithdrawnToken1: z.union([z.string(), z.number()]).transform((val) => {
    const num = typeof val === "string" ? Number(val) : val
    if (isNaN(num)) throw new Error(`Invalid totalWithdrawnToken1: ${val}`)
    return num
  }),
  totalCollectedToken0: z.union([z.string(), z.number()]).transform((val) => {
    const num = typeof val === "string" ? Number(val) : val
    if (isNaN(num)) throw new Error(`Invalid totalCollectedToken0: ${val}`)
    return num
  }),
  totalCollectedToken1: z.union([z.string(), z.number()]).transform((val) => {
    const num = typeof val === "string" ? Number(val) : val
    if (isNaN(num)) throw new Error(`Invalid totalCollectedToken1: ${val}`)
    return num
  }),
  totalCollectedUSD: z.union([z.string(), z.number()]).transform((val) => {
    const num = typeof val === "string" ? Number(val) : val
    if (isNaN(num)) throw new Error(`Invalid totalCollectedUSD: ${val}`)
    return num
  }),
  createdAtTimestamp: z.union([z.string(), z.number()]).transform((val) => {
    const num = typeof val === "string" ? Number(val) : val
    if (isNaN(num)) throw new Error(`Invalid createdAtTimestamp: ${val}`)
    return num
  }),
  createdAtBlockNumber: z.union([z.string(), z.number()]).transform((val) => {
    const num = typeof val === "string" ? Number(val) : val
    if (isNaN(num)) throw new Error(`Invalid createdAtBlockNumber: ${val}`)
    return num
  }),
  lastActivityTimestamp: z.union([z.string(), z.number()]).transform((val) => {
    const num = typeof val === "string" ? Number(val) : val
    if (isNaN(num)) throw new Error(`Invalid lastActivityTimestamp: ${val}`)
    return num
  }),
  lastActivityBlockNumber: z.union([z.string(), z.number()]).transform((val) => {
    const num = typeof val === "string" ? Number(val) : val
    if (isNaN(num)) throw new Error(`Invalid lastActivityBlockNumber: ${val}`)
    return num
  }),
  token0: z.object({
    id: z.string(),
    address: ethereumAddress,
    decimals: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid decimals: ${val}`)
      return num
    }),
    derivedETH: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid derivedETH: ${val}`)
      return num
    }),
    name: z.string(),
    symbol: z.string(),
  }),
  token1: z.object({
    id: z.string(),
    address: ethereumAddress,
    decimals: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid decimals: ${val}`)
      return num
    }),
    derivedETH: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid derivedETH: ${val}`)
      return num
    }),
    name: z.string(),
    symbol: z.string(),
  }),
  pool: z.object({
    id: z.string(),
    address: ethereumAddress,
    feeTier: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid feeTier: ${val}`)
      return num
    }),
    feesUSD: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid feesUSD: ${val}`)
      return num
    }),
    token0Price: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid token0Price: ${val}`)
      return num
    }),
    token1Price: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid token1Price: ${val}`)
      return num
    }),
    volumeUSD: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid volumeUSD: ${val}`)
      return num
    }),
    untrackedVolumeUSD: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid untrackedVolumeUSD: ${val}`)
      return num
    }),
    totalValueLockedUSD: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid totalValueLockedUSD: ${val}`)
      return num
    }),
    totalValueLockedToken0: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid totalValueLockedToken0: ${val}`)
      return num
    }),
    totalValueLockedToken1: z.union([z.string(), z.number()]).transform((val) => {
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid totalValueLockedToken1: ${val}`)
      return num
    }),
    liquidityProviderCount: z.union([z.string(), z.number()]).transform((val) => {
      return typeof val === "string" ? val : val.toString()
    }),
    liquidity: z.union([z.string(), z.number()]).transform((val) => {
      return typeof val === "string" ? val : val.toString()
    }),
    tick: z
      .union([z.string(), z.number(), z.null(), z.undefined()])
      .transform((val) => {
        if (val === null || val === undefined) return undefined
        const num = typeof val === "string" ? Number(val) : val
        if (isNaN(num)) return undefined
        return num
      })
      .optional(),
    sqrtPrice: z
      .union([z.string(), z.number(), z.null(), z.undefined()])
      .transform((val) => {
        if (val === null || val === undefined) return undefined
        try {
          return typeof val === "string" ? BigInt(val) : BigInt(Math.floor(val))
        } catch {
          return undefined
        }
      })
      .optional(),
  }),
})

const manualClMyPositions = z
  .object({
    Position: z.array(manualClMyPositionsItem),
    Bundle_by_pk: z
      .object({
        ethPriceUSD: z.union([z.string(), z.number()]).transform((val) => {
          const num = typeof val === "string" ? Number(val) : val
          if (isNaN(num)) throw new Error(`Invalid ethPriceUSD: ${val}`)
          return num
        }),
      })
      .nullable(),
  })
  .transform((data) => ({
    positions: data.Position,
    bundles: data.Bundle_by_pk ? [data.Bundle_by_pk] : [],
  }))

export const manualClMyPositionsResponseSchema = manualClMyPositions

export type ManualClMyPositionsResponse = z.infer<typeof manualClMyPositionsResponseSchema>
export type ManualClMyPositionsResponseItem = z.infer<typeof manualClMyPositionsItem>
//#endregion

//#region related token pools
const manualClRelatedTokenPoolsItem = z.object({
  id: ethereumAddress,
  feeTier: z.number(),
  totalValueLockedUSD: z.number(),
  token0: z.object({
    id: ethereumAddress,
    symbol: z.string(),
  }),
  token1: z.object({
    id: ethereumAddress,
    symbol: z.string(),
  }),
})

export const manualClRelatedTokenPoolsResponseSchema = z.object({
  pools: z.array(manualClRelatedTokenPoolsItem),
})

export type ManualClRelatedTokenPoolsResponse = Array<z.infer<typeof manualClRelatedTokenPoolsItem>>
export type ManualClRelatedTokenPoolsResponseItem = z.infer<typeof manualClRelatedTokenPoolsItem>
//#endregion

//#region getPoolActivity
/**
 * Activity feed shared shape for Swap, Mint, Burn and Collect events on a
 * given pool. Each event arrives with its `transaction.id` (hash) and
 * `logIndex` for BaseScan deep-links.
 *
 * Amounts are surfaced as numbers (Hasura returns strings); we keep them as
 * `number` because the feed is a display surface and sub-token precision is
 * not meaningful here. If you need exact amounts for replay, read the
 * transaction itself.
 */
const activityRowBase = z.object({
  id: z.string(),
  timestamp: stringOrNumberToNumber("timestamp"),
  amount0: stringOrNumberToNumber("amount0").optional(),
  amount1: stringOrNumberToNumber("amount1").optional(),
  amountUSD: stringOrNumberToNumber("amountUSD").optional(),
  origin: z.string().optional().nullable(),
  owner: z.string().optional().nullable(),
  sender: z.string().optional().nullable(),
  transaction: z
    .object({
      // `id` follows the multi-chain composite format
      // `<txHash>-<chainId>`; use `transactionHash` for links / display.
      id: z.string(),
      transactionHash: z.string().optional(),
    })
    .nullable()
    .optional(),
  logIndex: stringOrNumberToNumber("logIndex").optional(),
})

const manualClPoolActivityResponse = z
  .object({
    Swap: z.array(activityRowBase).optional().default([]),
    Mint: z.array(activityRowBase).optional().default([]),
    Burn: z.array(activityRowBase).optional().default([]),
    Collect: z.array(activityRowBase).optional().default([]),
  })
  .transform((data) => ({
    swaps: data.Swap.map((r) => ({ ...r, type: "Swap" as const })),
    mints: data.Mint.map((r) => ({ ...r, type: "Mint" as const })),
    burns: data.Burn.map((r) => ({ ...r, type: "Burn" as const })),
    collects: data.Collect.map((r) => ({ ...r, type: "Collect" as const })),
  }))

export const manualClPoolActivityResponseSchema = manualClPoolActivityResponse

export type ManualClPoolActivityResponse = z.infer<typeof manualClPoolActivityResponseSchema>
export type ManualClPoolActivityRow = ManualClPoolActivityResponse["swaps"][number]
//#endregion

//#region getPoolOHLC
const manualClPoolOhlcResponse = z
  .object({
    PoolHourData: z.array(poolIntervalItem).optional().default([]),
  })
  .transform((data) => ({ points: data.PoolHourData }))

export const manualClPoolOhlcResponseSchema = manualClPoolOhlcResponse
export type ManualClPoolOhlcResponse = z.infer<typeof manualClPoolOhlcResponseSchema>
export type ManualClPoolOhlcPoint = ManualClPoolOhlcResponse["points"][number]
//#endregion

//#region getPositionHistory
const positionHistoryRow = z.object({
  id: z.string(),
  timestamp: stringOrNumberToNumber("timestamp"),
  amount0: stringOrNumberToNumber("amount0").optional(),
  amount1: stringOrNumberToNumber("amount1").optional(),
  amountUSD: stringOrNumberToNumber("amountUSD").optional(),
  logIndex: stringOrNumberToNumber("logIndex").optional(),
  transaction: z
    .object({
      id: z.string(),
      transactionHash: z.string().optional(),
    })
    .nullable()
    .optional(),
})

const manualClPositionHistoryResponse = z
  .object({
    Position_by_pk: z
      .object({
        id: z.string(),
        mints: z.array(positionHistoryRow).optional().default([]),
        burns: z.array(positionHistoryRow).optional().default([]),
        collects: z.array(positionHistoryRow).optional().default([]),
      })
      .nullable(),
  })
  .transform((data) => {
    const position = data.Position_by_pk
    if (!position) return { positionId: undefined, history: [] }
    const rows = [
      ...position.mints.map((r) => ({ ...r, type: "Mint" as const })),
      ...position.burns.map((r) => ({ ...r, type: "Burn" as const })),
      ...position.collects.map((r) => ({ ...r, type: "Collect" as const })),
    ].sort((a, b) => b.timestamp - a.timestamp)
    return { positionId: position.id, history: rows }
  })

const nfpmHistoryRow = z.object({
  id: z.string(),
  timestamp: stringOrNumberToNumber("timestamp"),
  amount0: z.union([z.string(), z.number()]),
  amount1: z.union([z.string(), z.number()]),
  transactionHash: z.string(),
  logIndex: stringOrNumberToNumber("logIndex").optional(),
})

function rawTokenAmountToNumber(value: string | number, decimals: number): number {
  const raw = typeof value === "string" ? value : value.toString()
  const amount = Number(raw) / 10 ** decimals
  if (!Number.isFinite(amount)) return 0
  return amount
}

const manualClNfpmPositionHistoryResponse = z
  .object({
    NfpmPosition_by_pk: z
      .object({
        id: z.string(),
        token0: z.object({
          decimals: stringOrNumberToNumber("token0.decimals"),
          symbol: z.string().optional(),
        }),
        token1: z.object({
          decimals: stringOrNumberToNumber("token1.decimals"),
          symbol: z.string().optional(),
        }),
        increases: z.array(nfpmHistoryRow).optional().default([]),
        decreases: z.array(nfpmHistoryRow).optional().default([]),
        collects: z.array(nfpmHistoryRow).optional().default([]),
      })
      .nullable(),
  })
  .transform((data) => {
    const position = data.NfpmPosition_by_pk
    if (!position) return { positionId: undefined, history: [] }
    const toHistoryRow = (type: "Mint" | "Burn" | "Collect") => (row: z.infer<typeof nfpmHistoryRow>) => ({
      id: row.id,
      timestamp: row.timestamp,
      amount0: rawTokenAmountToNumber(row.amount0, position.token0.decimals),
      amount1: rawTokenAmountToNumber(row.amount1, position.token1.decimals),
      amountUSD: undefined,
      logIndex: row.logIndex,
      transaction: { id: row.transactionHash, transactionHash: row.transactionHash },
      type,
    })
    const rows = [
      ...position.increases.map(toHistoryRow("Mint")),
      ...position.decreases.map(toHistoryRow("Burn")),
      ...position.collects.map(toHistoryRow("Collect")),
    ].sort((a, b) => b.timestamp - a.timestamp)
    return {
      positionId: position.id,
      token0Symbol: position.token0.symbol,
      token1Symbol: position.token1.symbol,
      history: rows,
    }
  })

export const manualClPositionHistoryResponseSchema = manualClPositionHistoryResponse
export const manualClNfpmPositionHistoryResponseSchema = manualClNfpmPositionHistoryResponse
export type ManualClPositionHistoryResponse = z.infer<typeof manualClPositionHistoryResponseSchema> & {
  token0Symbol?: string
  token1Symbol?: string
}
export type ManualClPositionHistoryRow = ManualClPositionHistoryResponse["history"][number]
//#endregion

//#region getIndexerStatus
/**
 * Indexer freshness, mapped from Envio's built-in `chain_metadata` table.
 *
 * We deliberately source freshness from this table rather than from our
 * own `IndexerStatus` entity. The custom entity advances only when an
 * event handler fires, which makes it look stale on quiet chains / quiet
 * windows even when the indexer is fully caught up to head. That caused
 * the UI to wrongly display "significantly behind head" on a healthy
 * indexer.
 *
 * Field mapping:
 *   - `block_height`                   → `headBlock`     (indexer's view
 *                                                         of chain tip)
 *   - `latest_processed_block`         → `progressBlock` (committed to
 *                                                         DB; safe to
 *                                                         display)
 *   - `latest_fetched_block_number`    → `bufferBlock`   (fetched into
 *                                                         HyperSync, may
 *                                                         not yet be in
 *                                                         the DB)
 *
 * The banner compares `headBlock` against `progressBlock` so it only
 * warns when data the user can actually read is stale.
 */
const nullableStringOrNumber = (fieldName: string) =>
  z
    .union([z.string(), z.number(), z.null()])
    .optional()
    .transform((val) => {
      if (val === null || val === undefined) return undefined
      const num = typeof val === "string" ? Number(val) : val
      if (isNaN(num)) throw new Error(`Invalid ${fieldName}: ${val}`)
      return num
    })

const manualClIndexerStatusResponse = z
  .object({
    chain_metadata: z
      .array(
        z.object({
          chain_id: stringOrNumberToNumber("chain_id"),
          block_height: nullableStringOrNumber("block_height"),
          latest_processed_block: nullableStringOrNumber("latest_processed_block"),
          latest_fetched_block_number: nullableStringOrNumber("latest_fetched_block_number"),
          timestamp_caught_up_to_head_or_endblock: z.string().nullable().optional(),
        }),
      )
      .optional(),
  })
  .transform((data) => {
    const row = data.chain_metadata?.[0]
    if (!row) return null
    if (row.block_height === undefined || row.latest_processed_block === undefined) {
      return null
    }
    return {
      chainId: row.chain_id,
      headBlock: row.block_height,
      progressBlock: row.latest_processed_block,
      bufferBlock: row.latest_fetched_block_number ?? row.latest_processed_block,
      caughtUpAtIso: row.timestamp_caught_up_to_head_or_endblock ?? undefined,
    }
  })

export const manualClIndexerStatusResponseSchema = manualClIndexerStatusResponse
export type ManualClIndexerStatus = NonNullable<z.infer<typeof manualClIndexerStatusResponseSchema>>
//#endregion

//#region getMyNfpmPositions
/**
 * Indexer-native per-NFT entity (P7). Keyed by (nfpmAddress, tokenId, chainId)
 * so the frontend can map graph rows directly onto `ManualClPositionStore`
 * without the on-chain NFPM enumeration loop.
 *
 * `principal0/1` are already-computed underlying token amounts in raw wei
 * (BigInt), derived by the indexer from `pool.sqrtPrice + tickLower/Upper +
 * liquidity`. They replace the simulated `decreaseLiquidity` RPC for the
 * balance side of the position overview. Live pending fees (tokensOwed0/1)
 * are NOT indexable today — see `schema.graphql` P7 note — so the on-chain
 * stats query keeps running for fees only.
 */
const bigintString = (fieldName: string) =>
  z.union([z.string(), z.number()]).transform((val) => {
    try {
      return typeof val === "string" ? BigInt(val) : BigInt(Math.floor(val))
    } catch {
      throw new Error(`Invalid ${fieldName}: ${val}`)
    }
  })

const manualClNfpmPositionItem = z.object({
  id: z.string(),
  chainId: stringOrNumberToNumber("chainId"),
  nfpmAddress: z.string(),
  tokenId: bigintString("tokenId"),
  owner: z.string(),
  originalMinter: z.string().optional(),
  tickLower: bigintString("tickLower").transform((v) => Number(v)),
  tickUpper: bigintString("tickUpper").transform((v) => Number(v)),
  liquidity: bigintString("liquidity"),
  principal0: bigintString("principal0"),
  principal1: bigintString("principal1"),
  totalDeposited0: bigintString("totalDeposited0"),
  totalDeposited1: bigintString("totalDeposited1"),
  totalWithdrawn0: bigintString("totalWithdrawn0"),
  totalWithdrawn1: bigintString("totalWithdrawn1"),
  totalCollected0: bigintString("totalCollected0"),
  totalCollected1: bigintString("totalCollected1"),
  closed: z.boolean(),
  createdAtTimestamp: stringOrNumberToNumber("createdAtTimestamp"),
  createdAtBlockNumber: stringOrNumberToNumber("createdAtBlockNumber"),
  lastEventTimestamp: stringOrNumberToNumber("lastEventTimestamp"),
  lastEventBlockNumber: stringOrNumberToNumber("lastEventBlockNumber"),
  pool: z.object({
    id: z.string(),
    address: ethereumAddress,
    feeTier: stringOrNumberToNumber("feeTier"),
    tickSpacing: stringOrNumberToNumber("tickSpacing").optional(),
    liquidity: z
      .union([z.string(), z.number(), z.null(), z.undefined()])
      .transform((val) => {
        if (val === null || val === undefined) return undefined
        return typeof val === "string" ? val : val.toString()
      })
      .optional(),
    tick: z
      .union([z.string(), z.number(), z.null(), z.undefined()])
      .transform((val) => {
        if (val === null || val === undefined) return undefined
        const num = typeof val === "string" ? Number(val) : val
        if (isNaN(num)) return undefined
        return num
      })
      .optional(),
    sqrtPrice: z
      .union([z.string(), z.number(), z.null(), z.undefined()])
      .transform((val) => {
        if (val === null || val === undefined) return undefined
        try {
          return typeof val === "string" ? BigInt(val) : BigInt(Math.floor(val))
        } catch {
          return undefined
        }
      })
      .optional(),
  }),
  token0: z.object({
    id: z.string(),
    address: ethereumAddress,
    decimals: stringOrNumberToNumber("decimals"),
    symbol: z.string(),
  }),
  token1: z.object({
    id: z.string(),
    address: ethereumAddress,
    decimals: stringOrNumberToNumber("decimals"),
    symbol: z.string(),
  }),
})

const manualClNfpmPositionsResponse = z
  .object({
    NfpmPosition: z.array(manualClNfpmPositionItem).optional().default([]),
  })
  .transform((data) => ({ nfpmPositions: data.NfpmPosition }))

export const manualClNfpmPositionsResponseSchema = manualClNfpmPositionsResponse
export type ManualClNfpmPositionsResponse = z.infer<typeof manualClNfpmPositionsResponseSchema>
export type ManualClNfpmPositionRow = z.infer<typeof manualClNfpmPositionItem>
//#endregion
